// File: src/pages/Products.jsx

import React, { useEffect, useMemo, useState } from "react";
import { styled } from '@mui/material/styles';
import AdminLayout from "../components/AdminLayout";
import ProductImport from "../components/ProductImport";
import ProductDelete from "../components/ProductDelete";
import ProductExport from "../components/ProductExport";
import { db, storage } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Add as AddIcon,
  FileUpload as ImportIcon,
  Delete as BulkDeleteIcon,
  FileDownload as ExportIcon,
  Inventory2 as ProductsIcon
} from "@mui/icons-material";

// Styled Components
const ProductsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const ProductsHeader = styled('div')`
  margin-bottom: var(--space-lg);
`;

const PageTitle = styled('h1')`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 40px;
    height: 40px;
    color: var(--accent-primary);
  }
`;

const ActionsContainer = styled('div')`
  display: flex;
  gap: var(--space-md);
  align-items: center;
  margin-bottom: var(--space-xl);
  flex-wrap: wrap;
`;

const SearchContainer = styled('div')`
  margin-left: auto;
  min-width: 300px;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

const DataGridContainer = styled('div')`
  height: 600px;
  margin-top: var(--space-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;

  .MuiDataGrid-root {
    border: none;
    background: transparent;
    color: var(--text-primary);
  }

  .MuiDataGrid-columnHeaders {
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border);
  }

  .MuiDataGrid-columnHeader {
    color: var(--text-primary);
    font-weight: 600;
  }

  .MuiDataGrid-row {
    border-bottom: 1px solid var(--border-subtle);

    &:hover {
      background: var(--surface-hover);
    }
  }

  .MuiDataGrid-cell {
    color: var(--text-secondary);
    border-bottom: none;
  }

  .MuiDataGrid-footerContainer {
    background: var(--bg-tertiary);
    border-top: 1px solid var(--border);
    color: var(--text-secondary);
  }
`;

// Dark theme for Material-UI components
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#10b981',
      dark: '#059669',
      light: '#34d399',
    },
    secondary: {
      main: '#06b6d4',
    },
    error: {
      main: '#ef4444',
    },
    success: {
      main: '#22c55e',
    },
    warning: {
      main: '#f59e0b',
    },
    background: {
      default: '#0a0f0a',
      paper: '#2d342d',
    },
    text: {
      primary: '#f0f9f0',
      secondary: '#c8d8c8',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          },
        },
        outlined: {
          borderColor: '#10b981',
          color: '#10b981',
          '&:hover': {
            borderColor: '#34d399',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2d342d',
          backgroundImage: 'none',
          border: '1px solid #374151',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
          },
        },
      },
    },
  },
});

function Products() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Swallow benign ResizeObserver loop errors (Chrome) to avoid CRA red overlay
  React.useEffect(() => {
    const handler = (e) => {
      const msg = e?.message || e?.reason?.message || "";
      if (
        msg.includes("ResizeObserver loop completed with undelivered notifications") ||
        msg.includes("ResizeObserver loop limit exceeded")
      ) {
        e.stopImmediatePropagation?.();
      }
    };
    window.addEventListener("error", handler);
    window.addEventListener("unhandledrejection", handler);
    return () => {
      window.removeEventListener("error", handler);
      window.removeEventListener("unhandledrejection", handler);
    };
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    aisle: "",
    rack_level: "",
    image: null,
    category_id: "",
    barcode: "",
    keywords: "",
  });

  // --- Fetch ---
  const fetchProducts = async () => {
    const qs = await getDocs(collection(db, "products"));
    setProducts(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
  };
  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      const qs = await getDocs(collection(db, "categories"));
      setCategories(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetchCategories();
  }, []);

  // --- Maps for fast lookups ---
  const categoryNameById = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => m.set((c.id || "").trim(), (c.name || "").trim()));
    return m;
  }, [categories]);

  const categoryIdByNameLower = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => m.set((c.name || "").trim().toLowerCase(), (c.id || "").trim()));
    return m;
  }, [categories]);

  // --- Helpers ---
  const getAnyCategoryTokenFromRow = (row) => {
    // Why: historical rows may store category in different keys or formats.
    const raw = [
      row?.category_id,
      row?.categoryId,
      row?.category,
      row?.category_name,
    ]
      .find((v) => v !== undefined && v !== null && String(v).trim() !== "");
    return (raw ?? "").toString().trim();
  };

  const normalizeCategoryIdFromRow = (row) => {
    const token = getAnyCategoryTokenFromRow(row);
    if (!token) return "";
    // If it already looks like a Firestore doc id, keep it.
    if (categoryNameById.has(token)) return token;
    // Else treat token as a name and translate -> id.
    const id = categoryIdByNameLower.get(token.toLowerCase());
    return id || "";
  };

  const resolveCategoryNameFromRow = (row) => {
    const id = normalizeCategoryIdFromRow(row);
    if (!id) return "—"; // show dash so you can spot missing values
    return categoryNameById.get(id) || "—";
  };

  // Precompute a stable field that the grid can render/sort
  const rows = useMemo(
    () => {
      const productsWithCategory = (products || []).map((p) => ({
        ...p,
        category_name: resolveCategoryNameFromRow(p),
      }));

      // Apply search filter
      if (!searchQuery.trim()) {
        return productsWithCategory;
      }

      const query = searchQuery.toLowerCase().trim();
      return productsWithCategory.filter((product) => {
        const name = (product.name || "").toLowerCase();
        const description = (product.description || "").toLowerCase();
        const category = (product.category_name || "").toLowerCase();
        const barcode = (product.barcode || "").toLowerCase();
        const keywords = Array.isArray(product.keywords)
          ? product.keywords.join(" ").toLowerCase()
          : "";

        return (
          name.includes(query) ||
          description.includes(query) ||
          category.includes(query) ||
          barcode.includes(query) ||
          keywords.includes(query)
        );
      });
    },
    [products, categoryNameById, categoryIdByNameLower, searchQuery]
  );

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const generateCustomProductId = async () => {
    const snap = await getDocs(collection(db, "products"));
    const nums = snap.docs
      .map((d) => d.id.match(/^product(\d+)$/))
      .filter(Boolean)
      .map((m) => parseInt(m[1], 10))
      .sort((a, b) => a - b);
    return `product${nums.length ? nums[nums.length - 1] + 1 : 1}`;
  };

  const sanitizeFileName = (name) => name.toLowerCase().replace(/[^a-z0-9._-]+/gi, "-");

  const uploadImage = async (file, productId) => {
    if (!file) return { url: "", path: "" };
    const safe = sanitizeFileName(file.name);
    const unique = `${Date.now()}-${safe}`;
    const path = `products/${productId}/${unique}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type || undefined });
    const url = await getDownloadURL(storageRef);
    return { url, path };
  };

  const deleteImage = async (pathOrUrl) => {
    if (!pathOrUrl) return;
    try {
      let path = pathOrUrl;
      if (path.startsWith("https://")) {
        const m = decodeURIComponent(path).match(/\/o\/(.*?)\?/);
        if (m?.[1]) path = m[1];
      }
      await deleteObject(ref(storage, path));
    } catch (err) {
      console.warn("deleteImage warning:", err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      aisle: "",
      rack_level: "",
      image: null,
      category_id: "",
      barcode: "",
      keywords: "",
    });
    setEditId(null);
  };

  const handleSave = async () => {
    // Basic uniqueness checks (name & barcode)
    const qs = await getDocs(collection(db, "products"));
    const existing = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
    const dup = existing.find(
      (p) =>
        p.id !== editId &&
        (p.name?.toLowerCase() === formData.name.trim().toLowerCase() ||
          (formData.barcode && p.barcode === formData.barcode))
    );
    if (dup) {
      alert("Product name or barcode already exists.");
      return;
    }

    const category_id = (formData.category_id || "").trim();

    if (editId) {
      const snap = await getDoc(doc(db, "products", editId));
      const prev = snap.exists() ? snap.data() : {};
      let newImage = { url: "", path: "" };
      if (formData.image) newImage = await uploadImage(formData.image, editId);

      const newPrice = parseFloat(formData.price);
      const oldPrice = parseFloat(prev.price || newPrice);
      const wasManualDeal = prev.admin_override_deal === true;

      // Auto deal detection — only when price changes and no manual deal override
      let dealFields = {};
      if (!wasManualDeal) {
        if (newPrice < oldPrice) {
          const dropPercent = ((oldPrice - newPrice) / oldPrice) * 100;
          if (dropPercent >= 5) {
            const label =
              dropPercent >= 30 ? "Jimat Besar!" :
              dropPercent >= 15 ? "Price Drop Kaw-Kaw!" :
                                  "Harga Turun";
            const expiresAt = new Date(Date.now() + 14 * 86400000);
            dealFields = {
              is_deal: true,
              is_timed_promotion: false,
              promotional_price: null,
              previous_price: oldPrice,
              price_drop_percent: parseFloat(dropPercent.toFixed(1)),
              deal_label: label,
              deal_expires_at: Timestamp.fromDate(expiresAt),
              admin_override_deal: false,
            };
          } else {
            dealFields = {
              is_deal: false, is_timed_promotion: false, promotional_price: null,
              previous_price: null, price_drop_percent: 0,
              deal_label: "", deal_expires_at: null, admin_override_deal: false,
            };
          }
        } else if (newPrice > oldPrice) {
          dealFields = {
            is_deal: false, is_timed_promotion: false, promotional_price: null,
            previous_price: null, price_drop_percent: 0,
            deal_label: "", deal_expires_at: null, admin_override_deal: false,
          };
        }
      }

      await updateDoc(doc(db, "products", editId), {
        name: formData.name,
        description: formData.description,
        price: newPrice,
        aisle: parseInt(formData.aisle, 10),
        rack_level: parseInt(formData.rack_level, 10),
        category_id,
        barcode: formData.barcode,
        keywords: formData.keywords
          ? formData.keywords.split(",").map((k) => k.trim()).filter(Boolean)
          : [],
        ...(newImage.url && { image_url: newImage.url, image_path: newImage.path }),
        ...dealFields,
      });

      if (newImage.path && prev.image_path && prev.image_path !== newImage.path) {
        await deleteImage(prev.image_path);
      }
    } else {
      const newId = await generateCustomProductId();
      const newImage = await uploadImage(formData.image, newId);

      await setDoc(doc(db, "products", newId), {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        aisle: parseInt(formData.aisle, 10),
        rack_level: parseInt(formData.rack_level, 10),
        category_id,
        barcode: formData.barcode,
        keywords: formData.keywords
          ? formData.keywords.split(",").map((k) => k.trim()).filter(Boolean)
          : [],
        image_url: newImage.url || "",
        image_path: newImage.path || "",
      });
    }

    await fetchProducts();
    setOpen(false);
    resetForm();
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name || "",
      description: product.description || "",
      price: product.price ?? "",
      aisle: product.aisle ?? "",
      rack_level: product.rack_level ?? "",
      category_id: normalizeCategoryIdFromRow(product),
      barcode: product.barcode || "",
      keywords: Array.isArray(product.keywords) ? product.keywords.join(", ") : "",
      image: null,
    });
    setEditId(product.id);
    setOpen(true);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };
  const handleDelete = async () => {
    if (!deleteId) return;
    const snap = await getDoc(doc(db, "products", deleteId));
    const data = snap.exists() ? snap.data() : {};
    if (data.image_path) await deleteImage(data.image_path);
    else if (data.image_url) await deleteImage(data.image_url);
    await deleteDoc(doc(db, "products", deleteId));
    await fetchProducts();
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const columns = [
    { field: "name", headerName: "Name", width: 200, resizable: true },
    { field: "description", headerName: "Description", width: 300, resizable: true },
    { field: "price", headerName: "Price (RM)", width: 120, resizable: true },
    { field: "aisle", headerName: "Aisle", width: 80, resizable: true },
    { field: "rack_level", headerName: "Rack", width: 80, resizable: true },
    { field: "category_name", headerName: "Category", width: 180, resizable: true },
    { field: "barcode", headerName: "Barcode", width: 160, resizable: true },
    {
      field: "keywords",
      headerName: "Keywords",
      width: 250,
      resizable: true,
      renderCell: (params) => (Array.isArray(params.row?.keywords) ? params.row.keywords.join(", ") : ""),
    },
    {
      field: "image_url",
      headerName: "Image",
      width: 100,
      resizable: true,
      renderCell: (params) =>
        params.value ? (
          <img
            src={params.value}
            alt="product"
            width={50}
            height={50}
            style={{ objectFit: "cover", borderRadius: 6 }}
          />
        ) : null,
    },
    {
      field: "is_deal",
      headerName: "Deal",
      width: 80,
      sortable: false,
      resizable: false,
      renderCell: (params) =>
        params.value ? (
          <span style={{ color: "#ef4444", fontWeight: "bold" }}>🔥 Yes</span>
        ) : (
          <span style={{ color: "#6b7280" }}>—</span>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      resizable: false,
      renderCell: (params) => (
        <>
          <IconButton color="primary" size="small" onClick={() => handleEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" size="small" onClick={() => confirmDelete(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const handleImportComplete = () => {
    fetchProducts();
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <AdminLayout>
        <ProductsContainer>
          <ProductsHeader>
            <PageTitle>
              <ProductsIcon />
              Products Management
            </PageTitle>
          </ProductsHeader>

          <ActionsContainer>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Product
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ImportIcon />}
              onClick={() => setImportOpen(true)}
            >
              Import Products
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<BulkDeleteIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete Products
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<ExportIcon />}
              onClick={() => setExportOpen(true)}
            >
              Export Products
            </Button>

            <SearchContainer>
              <TextField
                size="small"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'var(--accent-primary)', mr: 1 }} />,
                }}
                sx={{ width: "100%" }}
              />
            </SearchContainer>
          </ActionsContainer>

          <DataGridContainer>
            <DataGrid
              rows={rows}
              columnResizeMode="onEnd"
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              getRowId={(r) => r.id}
              disableColumnMenu
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'var(--surface-hover)',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </DataGridContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editId ? "Edit Product" : "Add Product"}</DialogTitle>
        <DialogContent>
          <TextField label="Name" name="name" fullWidth value={formData.name} onChange={handleChange} margin="dense" />
          <TextField label="Description" name="description" fullWidth value={formData.description} onChange={handleChange} margin="dense" />
          <TextField label="Price" name="price" fullWidth type="number" value={formData.price} onChange={handleChange} margin="dense" />
          <TextField label="Aisle" name="aisle" fullWidth type="number" value={formData.aisle} onChange={handleChange} margin="dense" />
          <TextField label="Rack Level" name="rack_level" fullWidth type="number" value={formData.rack_level} onChange={handleChange} margin="dense" />
          <TextField label="Barcode" name="barcode" fullWidth value={formData.barcode} onChange={handleChange} margin="dense" />
          <TextField label="Keywords (comma separated)" name="keywords" fullWidth value={formData.keywords} onChange={handleChange} margin="dense" />

          <FormControl fullWidth style={{ marginTop: 15 }}>
            <InputLabel>Category</InputLabel>
            <Select name="category_id" value={formData.category_id} onChange={handleChange}>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <input type="file" name="image" accept="image/*" onChange={handleChange} style={{ marginTop: 15 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this product?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

        {/* Import Dialog */}
        <ProductImport
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImportComplete={handleImportComplete}
          categories={categories}
        />

        {/* Delete Dialog */}
        <ProductDelete
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onDeleteComplete={handleImportComplete}
          categories={categories}
        />

        {/* Export Dialog */}
        <ProductExport
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          categories={categories}
        />
        </ProductsContainer>
      </AdminLayout>
    </ThemeProvider>
  );
}

export default Products;

// ------------------------------------------------------------
// File: scripts/backfill-category-ids.js
// One-off script to normalize legacy category fields to `category_id`.
// Run in a Node environment with Firebase Admin SDK OR adapt to a protected admin page.
/*
import { initializeApp, applicationDefault } from "firebase-admin/app";
import { getFirestore } from "firebase-admin/firestore";

initializeApp({ credential: applicationDefault() });
const db = getFirestore();

async function backfill() {
  const catsSnap = await db.collection("categories").get();
  const idByNameLower = new Map();
  catsSnap.forEach((d) => idByNameLower.set((d.data().name || "").trim().toLowerCase(), d.id));

  const prodSnap = await db.collection("products").get();
  const ops = [];
  prodSnap.forEach((d) => {
    const p = d.data();
    const token = (p.category_id || p.categoryId || p.category || p.category_name || "").toString().trim();
    if (!token) return; // nothing to do
    const normalized = idByNameLower.get(token.toLowerCase()) || token; // if already an id, keep
    if (p.category_id !== normalized) {
      ops.push(db.collection("products").doc(d.id).update({ category_id: normalized }));
    }
  });
  await Promise.all(ops);
  console.log(`Updated ${ops.length} products.`);
}

backfill().catch(console.error);
*/
